import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Card,
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Progress,
  Alert,
  Space,
  Typography,
  Slider,
  Radio,
  Spin,
  Row,
  Col,
  message,
} from 'antd';
import { ArrowLeftOutlined, WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { checklistApi, strategyApi } from '@tradermind/shared/api';
import type { ChecklistQuestion } from '@tradermind/shared/types';

const { Title, Text, Paragraph } = Typography;

export default function PreTradeChecklistPage() {
  const { t } = useTranslation(['checklist', 'strategy']);
  const navigate = useNavigate();
  const [tradeInfo, setTradeInfo] = useState({
    symbol: '',
    direction: 'long' as 'long' | 'short',
    planned_size: 10,
  });
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showChecklist, setShowChecklist] = useState(false);
  // 用于强制生成新的检查清单
  const [checklistKey, _setChecklistKey] = useState(() => Date.now());
  // 策略相关状态
  const [selectedStrategyId, setSelectedStrategyId] = useState<number | null>(null);
  const [strategyConditionScores, setStrategyConditionScores] = useState<Record<string, number>>({});

  // 获取用户策略列表
  const { data: strategiesData } = useQuery({
    queryKey: ['strategies'],
    queryFn: () => strategyApi.getStrategies({ is_active: true, limit: 100 }),
  });
  const strategies = strategiesData?.data?.items || [];
  const selectedStrategy = strategies.find((s) => s.id === selectedStrategyId);

  const { data: checklistData, isLoading: isLoadingChecklist } = useQuery({
    queryKey: ['checklist', 'pre_trade', tradeInfo, checklistKey],
    queryFn: () =>
      checklistApi.generateChecklist({
        type: 'pre_trade',
        ...tradeInfo,
      }),
    enabled: showChecklist && !!tradeInfo.symbol,
    staleTime: 0, // 不缓存
  });

  const submitMutation = useMutation({
    mutationFn: checklistApi.submitChecklist,
    onSuccess: (response) => {
      navigate(`/checklist/result/${response.data.checklist_id}`);
    },
    onError: () => {
      message.error(t('messages.submit_failed'));
    },
  });

  const checklist = checklistData?.data;
  const questions = checklist?.questions || [];
  const assessment = checklist?.assessment;

  const translateMaybe = (raw?: string) => {
    if (!raw) return raw;
    const s = raw.trim();
    if (s.startsWith('checklist:') || s.startsWith('strategy:')) {
      return t(s);
    }
    if (s.startsWith('checklist.')) {
      return t(`checklist:${s.slice('checklist.'.length)}`);
    }
    if (s.startsWith('strategy.')) {
      return t(`strategy:${s.slice('strategy.'.length)}`);
    }

	// Some backends may return keys without namespace prefix, e.g. "basic_accept_loss.question"
	if (s.includes('.')) {
		const asChecklistKey = `checklist:${s}`;
		const checklistTranslated = t(asChecklistKey);
		if (checklistTranslated !== asChecklistKey && checklistTranslated !== s) {
			return checklistTranslated;
		}
		const asStrategyKey = `strategy:${s}`;
		const strategyTranslated = t(asStrategyKey);
		if (strategyTranslated !== asStrategyKey && strategyTranslated !== s) {
			return strategyTranslated;
		}
	}
    return raw;
  };

  // 计算进度：问题回答 + 策略条件评分
  const answeredCount = Object.keys(answers).filter((k) => answers[k]).length;
  const totalItems = questions.length + (selectedStrategy?.conditions?.length || 0);
  const scoredConditionsCount = selectedStrategy?.conditions?.filter(
    (c) => strategyConditionScores[c.id] !== undefined && strategyConditionScores[c.id] > 0
  ).length || 0;
  const completedItems = answeredCount + scoredConditionsCount;
  const progress = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;

  const handleStartChecklist = () => {
    if (!tradeInfo.symbol) {
      message.warning(t('messages.symbol_required'));
      return;
    }
    setShowChecklist(true);
  };

  const handleAnswer = (questionId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = () => {
    // 将 answers 对象转换为数组格式
    const answersArray = Object.entries(answers).map(([question_id, value]) => ({
      question_id,
      value,
      is_warning: false,
    }));

    // 构建策略条件评分数组
    const strategyConditionsArray = selectedStrategy?.conditions?.map((condition) => ({
      condition_id: condition.id,
      description: condition.description,
      score: strategyConditionScores[condition.id] || 0,
    })) || [];

    submitMutation.mutate({
      checklist_id: checklist?.checklist_id || 0,
      answers: answersArray,
      decision: 'proceed',
      strategy_id: selectedStrategyId || undefined,
      strategy_name: selectedStrategy?.name || undefined,
      strategy_conditions: strategyConditionsArray.length > 0 ? strategyConditionsArray : undefined,
    });
  };

  const renderQuestion = (question: ChecklistQuestion) => {
    const value = answers[question.id] || '';
    // 兼容后端返回的 question 字段和前端的 text 字段
    const questionText = translateMaybe((question as any).question || question.text) || '';
    const hintText = translateMaybe(question.hint);
    const placeholderText = translateMaybe(question.placeholder);
    const warningText = translateMaybe(question.warning);
    const suggestionText = translateMaybe((question as any).suggestion);
    // 兼容后端返回的 scale_min/scale_max 和前端的 min/max
    const scaleMin = (question as any).scale_min ?? question.min ?? 0;
    const scaleMax = (question as any).scale_max ?? question.max ?? 100;
    const scaleLabels = (question as any).scale_labels as string[] | undefined;
    const minLabel = translateMaybe((question as any).min_label);
    const maxLabel = translateMaybe((question as any).max_label);

    switch (question.type) {
      case 'text':
        return (
          <div key={question.id} style={{ marginBottom: 24 }}>
            <Text strong>{questionText}</Text>
            {hintText && (
              <div style={{ color: '#666', marginBottom: 8 }}>💡 {hintText}</div>
            )}
            <Input.TextArea
              value={value}
              onChange={(e) => handleAnswer(question.id, e.target.value)}
              rows={question.multiline ? 4 : 2}
              placeholder={placeholderText}
              autoComplete="off"
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
              <div style={{ color: value.length >= 30 ? '#10B981' : '#999', fontSize: 12 }}>
                {value.length >= 30 ? '🤖 ' + t('ai_analysis_enabled') : '💡 ' + t('ai_analysis_hint')}
              </div>
              <div style={{ color: question.min_length && value.length >= question.min_length ? '#10B981' : '#999' }}>
                {value.length} {t('chars')}{question.min_length ? ` (${t('min_required')}: ${question.min_length})` : ''}
              </div>
            </div>
          </div>
        );

      case 'number':
        return (
          <div key={question.id} style={{ marginBottom: 24 }}>
            <Text strong>{questionText}</Text>
            {hintText && (
              <div style={{ color: '#666', marginBottom: 8 }}>💡 {hintText}</div>
            )}
            {suggestionText && (
              <div style={{ color: '#3B82F6', marginBottom: 8 }}>💡 {t('system_suggestion')}: {suggestionText}</div>
            )}
            <InputNumber
              value={value ? parseFloat(value) : undefined}
              onChange={(v) => handleAnswer(question.id, v?.toString() || '')}
              style={{ width: '100%' }}
              placeholder={placeholderText}
              controls={true}
            />
          </div>
        );

      case 'scale':
      case 'slider':
        return (
          <div key={question.id} style={{ marginBottom: 24 }}>
            <Text strong>{questionText}</Text>
            {hintText && (
              <div style={{ color: '#666', marginBottom: 8 }}>💡 {hintText}</div>
            )}
            <Slider
              value={value ? parseFloat(value) : scaleMin}
              onChange={(v) => handleAnswer(question.id, v.toString())}
              min={scaleMin}
              max={scaleMax}
              step={question.step}
              marks={
                scaleLabels
                  ? scaleLabels.reduce((acc, label, idx) => {
                      const step = (scaleMax - scaleMin) / (scaleLabels.length - 1);
                      acc[scaleMin + idx * step] = translateMaybe(label) || label;
                      return acc;
                    }, {} as Record<number, string>)
                  : {
                      [scaleMin]: minLabel || `${scaleMin}`,
                      [scaleMax]: maxLabel || `${scaleMax}`,
                    }
              }
            />
            {question.optimal_range && (
              <div style={{ color: '#3B82F6'}}>💡 {t('optimal_range')}: {question.optimal_range}</div>
            )}
          </div>
        );

      case 'choice':
        // 兼容后端返回的 string[] 和前端期望的 {value, label}[]
        const options = question.options?.map((opt) => {
          if (typeof opt === 'string') {
            return { value: opt, label: translateMaybe(opt) || opt };
          }
          return {
            ...opt,
            label: translateMaybe((opt as any).label) || (opt as any).label,
          };
        }) || [];
        return (
          <div key={question.id} style={{ marginBottom: 24 }}>
            <Text strong>{questionText}</Text>
            {warningText && (
              <Alert message={warningText} type="warning" showIcon style={{ marginBottom: 8 }} />
            )}
            <Radio.Group
              value={value}
              onChange={(e) => handleAnswer(question.id, e.target.value)}
              style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
            >
              {options.map((option, idx) => (
                <Radio
                  key={`${question.id}-${option.value || idx}`}
                  value={option.value}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    background: option.risk_level === 'high' ? '#fef2f2' : undefined,
                  }}
                >
                  {option.label}
                  {option.risk_level === 'high' && <WarningOutlined style={{ color: '#EF4444', marginLeft: 8 }} />}
                </Radio>
              ))}
            </Radio.Group>
          </div>
        );

      case 'boolean':
        return (
          <div key={question.id} style={{ marginBottom: 24 }}>
            <Text strong>{questionText}</Text>
            <Radio.Group
              value={value}
              onChange={(e) => handleAnswer(question.id, e.target.value)}
              style={{ display: 'flex', gap: 8 }}
            >
              <Radio.Button value="yes">{t('yes')}</Radio.Button>
              <Radio.Button value="no">{t('no')}</Radio.Button>
            </Radio.Group>
          </div>
        );

      default:
        return null;
    }
  };

  if (!showChecklist) {
    return (
      <div>
        <div style={{ marginBottom: 24 }}>
          <Title level={4}>{t('title')}</Title>
          <Text type="secondary">{t('description')}</Text>
        </div>

        <Card title={t('trade_info')} style={{ maxWidth: 600 }}>
          <Form layout="vertical">
            <Form.Item label={t('symbol')} required>
              <Input
                value={tradeInfo.symbol}
                onChange={(e) => setTradeInfo({ ...tradeInfo, symbol: e.target.value })}
                placeholder={t('symbol_placeholder')}
              />
            </Form.Item>
            <Form.Item label={t('direction')}>
              <Select
                value={tradeInfo.direction}
                onChange={(v) => setTradeInfo({ ...tradeInfo, direction: v })}
                options={[
                  { value: 'long', label: t('direction_long') },
                  { value: 'short', label: t('direction_short') },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('planned_size')}>
              <Slider
                value={tradeInfo.planned_size}
                onChange={(v) => setTradeInfo({ ...tradeInfo, planned_size: v })}
                min={1}
                max={100}
                marks={{
                  1: t('planned_size_marks.1'),
                  25: t('planned_size_marks.25'),
                  50: t('planned_size_marks.50'),
                  100: t('planned_size_marks.100')
                }}
              />
            </Form.Item>

            {strategies.length > 0 && (
              <Form.Item label={t('strategy:select_strategy')}>
                <Select
                  value={selectedStrategyId}
                  onChange={setSelectedStrategyId}
                  placeholder={t('strategy:no_strategy')}
                  allowClear
                  options={[
                    ...strategies.map((s) => ({
                      value: s.id,
                      label: `${s.name} (${s.conditions?.length || 0} ${t('strategy:conditions')})`,
                    })),
                  ]}
                />
                {selectedStrategy && (
                  <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                    {selectedStrategy.description}
                  </Text>
                )}
              </Form.Item>
            )}

            <Button type="primary" size="large" block onClick={handleStartChecklist}>
              {t('start_check')}
            </Button>
          </Form>
        </Card>
      </div>
    );
  }

  if (isLoadingChecklist) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>{t('generating')}</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => setShowChecklist(false)}>
            {t('back')}
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {tradeInfo.symbol} - {tradeInfo.direction === 'long' ? t('direction_long') : t('direction_short')}
          </Title>
        </Space>
      </div>

      <Row gutter={24}>
        <Col xs={24} lg={16}>
          <Card>
            <div style={{ marginBottom: 24 }}>
              <Text>{t('progress')}</Text>
              <Progress percent={Math.round(progress)} status={progress === 100 ? 'success' : 'active'} />
            </div>

            {selectedStrategy && selectedStrategy.conditions && selectedStrategy.conditions.length > 0 && (
              <Card
                title={`📋 ${t('strategy:strategy_score')}`}
                style={{ marginBottom: 24, backgroundColor: '#f0f9ff' }}
              >
                <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                  {t('strategy:condition_score_hint')}
                </Paragraph>
                {selectedStrategy.conditions.map((condition) => (
                  <div key={condition.id} style={{ marginBottom: 16 }}>
                    <Text strong>{condition.description}</Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
                      <Slider
                        style={{ flex: 1 }}
                        min={0}
                        max={10}
                        value={strategyConditionScores[condition.id] || 0}
                        onChange={(value) =>
                          setStrategyConditionScores((prev) => ({
                            ...prev,
                            [condition.id]: value,
                          }))
                        }
                        marks={{
                          0: t('strategy:condition_score_marks.0'),
                          5: t('strategy:condition_score_marks.5'),
                          10: t('strategy:condition_score_marks.10')
                        }}
                      />
                      <Text strong style={{ minWidth: 30 }}>
                        {strategyConditionScores[condition.id] || 0}
                      </Text>
                    </div>
                  </div>
                ))}
              </Card>
            )}

            {questions.map(renderQuestion)}

            <Button
              type="primary"
              size="large"
              block
              onClick={handleSubmit}
              loading={submitMutation.isPending}
              disabled={progress < 100}
            >
              {t('complete_check')}
            </Button>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          {assessment && (
            <Card title={`📊 ${t('assessment.title')}`}>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div style={{ fontSize: 48, fontWeight: 700, color: assessment.predicted_win_rate >= 50 ? '#10B981' : '#EF4444' }}>
                  {assessment.predicted_win_rate}%
                </div>
                <Text type="secondary">{t('assessment.win_rate')}</Text>
                <div style={{ marginTop: 8 }}>
                  {assessment.predicted_win_rate > assessment.avg_win_rate ? (
                    <Text type="success">↑ {t('assessment.above_avg')} ({assessment.avg_win_rate}%)</Text>
                  ) : (
                    <Text type="danger">↓ {t('assessment.below_avg')} ({assessment.avg_win_rate}%)</Text>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <Text strong>{t('assessment.risk_factors')}:</Text>
                {assessment.risk_factors.map((factor, index) => (
                  <div key={index} style={{ marginTop: 8 }}>
                    {factor.is_positive ? (
                      <Text type="success">✓ {factor.description}</Text>
                    ) : (
                      <Text type="warning">⚠️ {factor.description}</Text>
                    )}
                  </div>
                ))}
              </div>

              {assessment.suggestions.length > 0 && (
                <div>
                  <Text strong>💡 {t('assessment.suggestions')}:</Text>
                  {assessment.suggestions.map((suggestion, index) => (
                    <Paragraph key={index} style={{ marginTop: 8, marginBottom: 0 }}>
                      {suggestion}
                    </Paragraph>
                  ))}
                </div>
              )}
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
}
